import {
  DqCard,
  DqCardContent,
  DqIcon,
  type DqIconProps,
} from "@decentriq/components";
import { faBinoculars, faSliders } from "@fortawesome/pro-light-svg-icons";
import { Button, Grid, RadioGroup, Typography, useTheme } from "@mui/joy";
import { memo, useMemo, useState } from "react";
import RemarketingIcon from "features/dataRoomCreation/assets/remarketing.svg";
import {
  AdvertiserAudienceGeneratorType,
  useAdvertiserAudienceGenerator,
} from "../../contexts/AdvertiserAudienceGeneratorContext";
import {
  AdvertiserAudienceGeneratorHeader,
  AudienceGeneratorStepActionsWrapper,
  AudienceGeneratorStepSectionDescription,
  AudienceGeneratorStepSectionTitle,
} from "..";

type AudienceGeneratorType = {
  value: AdvertiserAudienceGeneratorType;
  title: string;
  description: string;
  icon: DqIconProps["icon"];
  iconSize?: string;
};

const audienceGeneratorTypeOptions: AudienceGeneratorType[] = [
  {
    description: "Re-engage users from the seed audience.",
    icon: <RemarketingIcon />,
    title: "Remarketing audience",
    value: AdvertiserAudienceGeneratorType.REMARKETING,
  },
  {
    description: "Combine and filter audiences.",
    icon: faSliders,
    iconSize: "2x",
    title: "Rule-based audience",
    value: AdvertiserAudienceGeneratorType.RULES_BASED,
  },
  {
    description:
      "Build an audience consisting of the users most similar to the seed audience.",
    icon: faBinoculars,
    iconSize: "2x",
    title: "AI lookalike audience",
    value: AdvertiserAudienceGeneratorType.LOOKALIKE,
  },
];

const AdvertiserAudienceGeneratorTypeSelection = memo(() => {
  const { palette } = useTheme();
  const { handleBackStep, selectGeneratorType, availableGeneratorTypes } =
    useAdvertiserAudienceGenerator();
  const [generatorType, setGeneratorType] =
    useState<AdvertiserAudienceGeneratorType | null>(null);
  const generatorTypes = useMemo(
    () =>
      audienceGeneratorTypeOptions.filter(({ value }) =>
        availableGeneratorTypes.includes(value)
      ),
    [availableGeneratorTypes]
  );
  return (
    <RadioGroup
      name="generatorType"
      onChange={(event) =>
        setGeneratorType(event.target.value as AdvertiserAudienceGeneratorType)
      }
      value={generatorType}
    >
      <Grid container={true}>
        <Grid mb={3} md={7} xs={12}>
          <AdvertiserAudienceGeneratorHeader />
        </Grid>
        <Grid mb={2} md={7} xs={12}>
          <AudienceGeneratorStepSectionTitle>
            1. Audience type
          </AudienceGeneratorStepSectionTitle>
          <AudienceGeneratorStepSectionDescription>
            Select the type of audience to create.
          </AudienceGeneratorStepSectionDescription>
        </Grid>
        <Grid
          columnSpacing={2}
          container={true}
          mb={2}
          md={7}
          rowSpacing={2}
          xs={12}
        >
          {generatorTypes.map(
            ({ description, icon, title, value, iconSize }) => (
              <Grid key={value} md={12 / generatorTypes.length} xs={12}>
                <DqCard
                  key={value}
                  selectable={{
                    component: "radio",
                    value,
                  }}
                >
                  <DqCardContent gap={1}>
                    <DqIcon
                      icon={icon}
                      iconProps={{
                        color: palette.primary[500],
                        size: iconSize,
                      }}
                    />
                    <Typography level="title-md" textAlign="center">
                      {title}
                    </Typography>
                    <Typography level="body-sm" textAlign="center">
                      {description}
                    </Typography>
                  </DqCardContent>
                </DqCard>
              </Grid>
            )
          )}
        </Grid>
        <AudienceGeneratorStepActionsWrapper>
          <Button onClick={handleBackStep} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={generatorType === null}
            onClick={() => selectGeneratorType(generatorType!)}
            variant="solid"
          >
            Continue
          </Button>
        </AudienceGeneratorStepActionsWrapper>
      </Grid>
    </RadioGroup>
  );
});

AdvertiserAudienceGeneratorTypeSelection.displayName =
  "AdvertiserAudienceGeneratorTypeSelection";

export default AdvertiserAudienceGeneratorTypeSelection;
